import { useState, useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { extractFirstUrl } from '@/lib/linkify'

interface UrlPreviewData {
  url: string
  title?: string
  description?: string
  image?: string
  site_name?: string
  favicon?: string
  error?: string
}

// Cache for URL previews to avoid refetching
const urlPreviewCache: Record<string, UrlPreviewData | null> = {}

interface UrlPreviewProps {
  url: string
  className?: string
}

export function UrlPreview({ url, className }: UrlPreviewProps) {
  const [preview, setPreview] = useState<UrlPreviewData | null>(null)
  const [loading, setLoading] = useState(true)
  const [imageError, setImageError] = useState(false)

  useEffect(() => {
    // Check cache first
    if (urlPreviewCache[url] !== undefined) {
      setPreview(urlPreviewCache[url])
      setLoading(false)
      return
    }

    const fetchPreview = async () => {
      try {
        const response = await fetch(`/url-preview?url=${encodeURIComponent(url)}`)
        const data = await response.json()

        if (data.error) {
          urlPreviewCache[url] = null
          setPreview(null)
        } else {
          urlPreviewCache[url] = data
          setPreview(data)
        }
      } catch {
        urlPreviewCache[url] = null
        setPreview(null)
      } finally {
        setLoading(false)
      }
    }

    fetchPreview()
  }, [url])

  if (loading) {
    return (
      <div className={cn(
        "mt-2 p-3 rounded-lg bg-muted/30 border-l-2 border-primary animate-pulse",
        className
      )}>
        <div className="h-3 w-3/5 bg-muted rounded mb-2" />
        <div className="h-2.5 w-4/5 bg-muted/80 rounded" />
      </div>
    )
  }

  if (!preview || preview.error) {
    return null
  }

  const hasImage = preview.image && !imageError
  const hostname = (() => {
    try {
      return new URL(url).hostname.replace('www.', '')
    } catch {
      return ''
    }
  })()

  return (
    <a
      href={preview.url || url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "block mt-2 rounded-lg overflow-hidden bg-card border border-border",
        "hover:border-border/80 hover:shadow-sm transition-all duration-150",
        "no-underline text-inherit",
        className
      )}
    >
      {/* Image */}
      {hasImage && (
        <div className="w-full h-28 bg-muted/30 overflow-hidden">
          <img
            src={preview.image}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        </div>
      )}

      {/* Content */}
      <div className="p-2.5">
        {/* Site info */}
        <div className="flex items-center gap-1.5 mb-1">
          {preview.favicon && (
            <img
              src={preview.favicon}
              alt=""
              className="size-3.5 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          )}
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
            {preview.site_name || hostname}
          </span>
        </div>

        {/* Title */}
        {preview.title && (
          <div className="text-xs font-semibold text-foreground line-clamp-2 leading-tight mb-0.5">
            {preview.title}
          </div>
        )}

        {/* Description */}
        {preview.description && (
          <div className="text-[11px] text-muted-foreground line-clamp-2 leading-snug">
            {preview.description}
          </div>
        )}
      </div>
    </a>
  )
}

/**
 * Hook to get the first URL from a message for preview
 * Uses linkifyjs for proper URL detection
 */
export function useMessageUrls(content: string): string | null {
  return useMemo(() => extractFirstUrl(content), [content])
}

export default UrlPreview
